import { API_BASE } from './client';
import type { FeedbackEvent } from './types';

/**
 * Recommendation feedback: which tickets a rider viewed, opened on AniList, or
 * skipped, reported against the /recommend answer's `request_id`. It is what a
 * future ranker learns from — which picks actually appealed, which the offline
 * metrics cannot tell us.
 *
 * Events are queued and sent in batches as beacons, so a rider closing the tab
 * mid-ride still reports the ticket they were on. Nothing is sent when the
 * response carried no request_id (the log is off, or the demo rider) or when the
 * browser asks not to be tracked.
 */

const MAX_BATCH = 50; // the backend's per-request cap
const FLUSH_AT = 20;
const FLUSH_AFTER_MS = 10_000;
const MIN_VIEW_MS = 250;

let requestId: string | null = null;
let queue: FeedbackEvent[] = [];
let timer: ReturnType<typeof setTimeout> | undefined;
let pendingView: { event: FeedbackEvent; since: number } | null = null;

function optedOut(): boolean {
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.globalPrivacyControl === true || nav.doNotTrack === '1';
}

/** Start reporting against a new answer (or stop, with null). */
export function beginFeedback(id: string | null | undefined): void {
  endView();
  flushFeedback();
  requestId = id && !optedOut() ? id : null;
}

export function trackFeedback(event: FeedbackEvent): void {
  if (!requestId) return;
  queue.push(event);
  if (queue.length >= FLUSH_AT) flushFeedback();
  else if (timer === undefined) timer = setTimeout(flushFeedback, FLUSH_AFTER_MS);
}

/**
 * A ticket came on screen. Its `view` event is emitted when it leaves (or the
 * page is hidden), carrying how long it was shown.
 */
export function beginView(event: Omit<FeedbackEvent, 'type' | 'dwell_ms'>): void {
  endView();
  if (requestId) pendingView = { event: { ...event, type: 'view' }, since: performance.now() };
}

export function endView(): void {
  if (!pendingView) return;
  const { event, since } = pendingView;
  pendingView = null;
  const dwell = Math.round(performance.now() - since);
  // Under a quarter second nobody read it: a flick past, or the one frame a
  // station change renders the old position against the new line.
  if (dwell >= MIN_VIEW_MS) trackFeedback({ ...event, dwell_ms: dwell });
}

export function flushFeedback(): void {
  if (timer !== undefined) {
    clearTimeout(timer);
    timer = undefined;
  }
  if (!requestId) {
    queue = [];
    return;
  }
  while (queue.length) {
    send({ request_id: requestId, events: queue.splice(0, MAX_BATCH) });
  }
}

function send(body: { request_id: string; events: FeedbackEvent[] }): void {
  const url = `${API_BASE}/events`;
  const json = JSON.stringify(body);
  try {
    if (navigator.sendBeacon?.(url, new Blob([json], { type: 'application/json' }))) return;
  } catch {
    // Some browsers throw on oversized beacons; fall through to fetch.
  }
  void fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: json,
    keepalive: true,
  }).catch(() => undefined);
}

// Leaving or backgrounding the page is the last reliable moment to report.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      endView();
      flushFeedback();
    }
  });
  window.addEventListener('pagehide', () => {
    endView();
    flushFeedback();
  });
}
